import { config } from '@config'
import { action, observable } from 'mobx'
import { VIPChannel } from '@modules/common/models/enums/VIPChannel'
import { apolloClient, strings } from '@stores'
import { GET_CLUSTERS } from '@modules/wholesale/clusterSetting/graphql/getClusters'
import { BULK_OPERATION_TABS as tabs } from '@modules/collection/constants'
import {
  isRetailChannel,
  isWholesaleChannel,
  isWholesaleChannelUserRole,
  isRetailChannelUserRole,
  isRetailChannelClosed,
  isWholesaleChannelClosed
} from '@services/wholesale/wholesaleBuyingSessionGroupsServices'
import { computedFn } from 'mobx-utils'
import { SalesPeriodSettingsDataStore } from '@modules/wholesale/salesPeriodSettings/stores/salesPeriodDataStore'
import { hasUserRoles, PERMISSION_MAP } from '@services/userRoleService'
import { DefaultConfig } from '@constants'

export class BuyingSessionSidebarStore {
  private salesPeriodSettingsDataStore = new SalesPeriodSettingsDataStore()

  @observable selectedChannelsValues = []
  @observable wholesaleClusters = []
  @observable retailClusters = []
  @observable isClustersLoading = false
  @observable salesPeriods = []
  @observable isSalesPeriodLoading = false

  // For initial render
  getAllClustersData = async channels => {
    try {
      this.isClustersLoading = true
      const queries = []
      channels.forEach(channel => {
        queries.push(
          apolloClient.query({
            query: GET_CLUSTERS,
            variables: {
              channel
            },
            fetchPolicy: 'network-only'
          })
        )
      })
      const data = await Promise.all(queries)
      data.forEach(response => {
        // Clusters
        const clusters = response?.data?.findVIPCluster?.Items ?? []
        if (clusters.length && clusters[0]?.Channel === VIPChannel.Wholesale) {
          this.wholesaleClusters = clusters
        } else if (clusters.length && clusters[0]?.Channel === VIPChannel.Retail) {
          this.retailClusters = clusters
        }
      })
    } finally {
      this.isClustersLoading = false
    }
  }

  @action setSelectedChannelValues = (
    channels,
    buyingSessionGroupStatuses?: Record<string, string>
  ) => {
    const val = channels.reduce((acc, channel) => {
      const isChannelClosed =
        channel === VIPChannel.Retail
          ? isRetailChannelClosed(buyingSessionGroupStatuses?.[channel])
          : isWholesaleChannelClosed(buyingSessionGroupStatuses?.[channel])

      if (!isChannelClosed) {
        acc.push({ Name: channel.split(':')[1], Value: channel, Id: channel })
      }
      return acc
    }, [])
    this.selectedChannelsValues = val
  }

  @action
  resetTabContentData = () => {
    this.selectedChannelsValues = []
    this.wholesaleClusters = []
    this.retailClusters = []
    this.isClustersLoading = false
    this.salesPeriods = []
    this.isSalesPeriodLoading = false
  }

  // bulk popup tabs based on available channels
  tabHeaders = computedFn(
    (
      channels,
      authorizedUserRolesByChannel,
      buyingSessionGroupStatuses: Record<string, string>
    ) => {
      const allowedRoles = PERMISSION_MAP.find(
        ele => ele.id === VIPChannel.Wholesale
      )?.allowedRoles
      const canEditWholesale =
        hasUserRoles(allowedRoles) &&
        !isWholesaleChannelClosed(buyingSessionGroupStatuses?.[VIPChannel.Wholesale])
      const allowedRolesRetail = PERMISSION_MAP.find(
        ele => ele.id === VIPChannel.Retail
      )?.allowedRoles
      const canEditRetail =
        hasUserRoles(allowedRolesRetail) &&
        !isRetailChannelClosed(buyingSessionGroupStatuses?.[VIPChannel.Retail])
      return tabs.filter(tab => {
        if (
          tab.id === strings.tagTypes.wholesaleCluster ||
          tab.id === strings.tagTypes.drop ||
          tab.id === strings.tagTypes.wholesaleFreeTags ||
          tab.id === strings.tagTypes.clientExclusions
        ) {
          return (
            isWholesaleChannel(channels) &&
            isWholesaleChannelUserRole(authorizedUserRolesByChannel) &&
            canEditWholesale &&
            tab
          )
        } else if (tab.id === strings.tagTypes.wholesaleClusterExclusions) {
          return (
            (config.bulkUpdateClusterExclusion ||
              DefaultConfig.BULK_UPDATE_CLUSTER_EXCLUSION) === 'true' &&
            isWholesaleChannel(channels) &&
            isWholesaleChannelUserRole(authorizedUserRolesByChannel) &&
            canEditWholesale &&
            tab
          )
        } else if (tab.id === strings.tagTypes.retailClusterExclusions) {
          return (
            (config.bulkUpdateClusterExclusion ||
              DefaultConfig.BULK_UPDATE_CLUSTER_EXCLUSION) === 'true' &&
            isRetailChannel(channels) &&
            isRetailChannelUserRole(authorizedUserRolesByChannel) &&
            canEditRetail &&
            tab
          )
        } else if (
          tab.id === strings.tagTypes.retailCluster ||
          tab.id === strings.tagTypes.retailFreeTags ||
          tab.id === strings.tagTypes.salesPeriod ||
          tab.id === strings.tagTypes.zoneExclusions
        ) {
          return (
            isRetailChannel(channels) &&
            isRetailChannelUserRole(authorizedUserRolesByChannel) &&
            canEditRetail &&
            tab
          )
        } else {
          return tab
        }
      })
    }
  )

  @action
  getSalesPeriods = async bsgId => {
    try {
      this.isSalesPeriodLoading = true
      const data = await this.salesPeriodSettingsDataStore.getSalesPeriods(bsgId)
      if (data.salesPeriods?.length) {
        this.salesPeriods = data.salesPeriods
      }
    } finally {
      this.isSalesPeriodLoading = false
    }
  }
}
